/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import React from 'react';

import classnames from 'classnames';

let minInput: React.RefObject<HTMLInputElement>;

let maxInput: React.RefObject<HTMLInputElement>;

/**
 * Class names used in component.
 */
const componentClassName = 'msc-refiner-item-daterange';
const minLabelClassName = `${componentClassName}__label-min`;
const maxLabelClassName = `${componentClassName}__label-max`;
const minInputClassName = `${componentClassName}__input-min`;
const maxInputClassName = `${componentClassName}__input-max`;
const minLabelId = `${componentClassName}__label-min`;
const maxLabelId = `${componentClassName}__label-max`;
const minInputLabelId = `${componentClassName}__input-min`;
const maxInputLabelId = `${componentClassName}__input-max`;
const validationErrorMessageClassName = `${componentClassName}__error`;

/**
 * IRefinerItemDateRangeComponent interface.
 */
interface IRefinerItemDateRangeComponent extends IComponent<IRefinerItemDateRangeProps> {}

/**
 * IRefinerItemDateRangeProps interface.
 */
export interface IRefinerItemDateRangeProps extends IComponentProps<{}> {
    className?: string;
    minInputLabel?: string;
    maxInputLabel?: string;
    minInputAriaLabel?: string;
    maxInputAriaLabel?: string;
    isDisabled?: boolean;
    minRangeValue?: string;
    maxRangeValue?: string;
    validationErrorForRange?: string;
    fallbackPattern?: string;
    onChangeMinValue?(minValue: string): void;
    onChangeMaxValue?(maxValue: string): void;
    onBlurMinInput?(minValue: string): void;
    onBlurMaxInput?(maxValue: string): void;
}

/**
 * RefinerItemDateRange component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const RefinerItemDateRangeFunctionalComponent: React.FC<IRefinerItemDateRangeProps> = (props: IRefinerItemDateRangeProps) => {
    const {
        className,
        minInputLabel,
        maxInputLabel,
        minInputAriaLabel,
        maxInputAriaLabel,
        isDisabled,
        minRangeValue,
        maxRangeValue,
        validationErrorForRange,
        fallbackPattern,
        onChangeMinValue,
        onChangeMaxValue,
        onBlurMinInput,
        onBlurMaxInput
    } = props;
    const [selectedMinValue, setSelectedMinValue] = React.useState(props.minRangeValue);
    const [selectedMaxValue, setSelectedMaxValue] = React.useState(props.maxRangeValue);
    const [validationErrorMessageForRange, setValidationErrorMessageForRange] = React.useState('');

    const onChangeMinValueHandler = (event: React.FocusEvent<HTMLInputElement>): void => {
        const selectedFormattedValue = event.currentTarget.value;
        setSelectedMinValue(selectedFormattedValue);
        onChangeMinValue?.(selectedFormattedValue);
    };

    const onChangeMaxValueHandler = (event: React.FocusEvent<HTMLInputElement>): void => {
        const selectedFormattedValue = event.currentTarget.value;
        setSelectedMaxValue(selectedFormattedValue);
        onChangeMaxValue?.(selectedFormattedValue);
    };

    function validateRange(minimum?: string, maximum?: string): boolean {
        if (minimum && maximum && minimum > maximum) {
            setValidationErrorMessageForRange(validationErrorForRange ?? '');
            return false;
        }
        setValidationErrorMessageForRange('');
        return true;
    }

    const onBlurMinValueHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        const userSelectedMinValue = event.currentTarget.value;
        setSelectedMinValue(userSelectedMinValue);
        const updatedMinInput = userSelectedMinValue;
        if (validateRange(updatedMinInput, maxRangeValue)) {
            onBlurMinInput?.(updatedMinInput);
        }
    };

    const onBlurMaxValueHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        const userSelectedMaxValue = event.currentTarget.value;
        setSelectedMaxValue(userSelectedMaxValue);
        const updatedMaxInput = userSelectedMaxValue;
        if (validateRange(minRangeValue, updatedMaxInput)) {
            onBlurMaxInput?.(updatedMaxInput);
        }
    };

    return (
        <form
            className={classnames(className, componentClassName)}
            aria-disabled={isDisabled}
            data-testid='range-refine-input-item-container'
        >
            <label id={minLabelId} htmlFor={minInputLabelId} className={minLabelClassName}>
                {minInputLabel}
                <input
                    className={minInputClassName}
                    type='date'
                    onChange={onChangeMinValueHandler}
                    onBlur={onBlurMinValueHandler}
                    value={selectedMinValue}
                    id={minInputLabelId}
                    ref={minInput}
                    aria-label={minInputAriaLabel ?? minInputLabel}
                    aria-disabled={isDisabled}
                    pattern={fallbackPattern}
                />
            </label>
            <label id={maxLabelId} htmlFor={maxInputLabelId} className={maxLabelClassName}>
                {maxInputLabel}
                <input
                    className={maxInputClassName}
                    type='date'
                    onChange={onChangeMaxValueHandler}
                    onBlur={onBlurMaxValueHandler}
                    value={selectedMaxValue}
                    id={maxInputLabelId}
                    ref={maxInput}
                    aria-label={maxInputAriaLabel ?? maxInputLabel}
                    aria-disabled={isDisabled}
                    pattern={fallbackPattern}
                />
            </label>
            {validationErrorMessageForRange && validationErrorMessageForRange !== '' && (
                <span className={validationErrorMessageClassName}>{validationErrorMessageForRange}</span>
            )}
        </form>
    );
};

/**
 * RefinerItemDateRange component.
 */
export const RefinerItemDateRangeComponent: React.FunctionComponent<IRefinerItemDateRangeProps> = msdyn365Commerce.createComponent<
    IRefinerItemDateRangeComponent
>('Refiner-Item-DateRange', { component: React.memo(RefinerItemDateRangeFunctionalComponent) });
